import React from "react";
import MoveChildNode from "./MoveChildNode";
import { calculateGlobalNodeNumberByType, compareSiblingOrders } from "./utils";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";

/**
 * MoveSubstepNode Component
 * Renders a substep for move position selection
 */
const MoveSubstepNode = ({
  substep,
  substepIndex,
  globalStepNumber,
  sectionIndex,
  sectionId,
  stepId,
  preloadedData,
  checkboxState,
  moveNodeType,
  checkboxLabelColor,
  customOnNavigate,
  shouldDisableSubstepCheckbox,
  shouldShowSubstepCheckbox,
  shouldDisableChildCheckbox,
  shouldShowChildCheckbox,
}) => {
  const substepLetter = String.fromCharCode(65 + substepIndex);
  const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

  // Get step_id_ref for comparison
  const step = preloadedData
    .flatMap(s => s.steps || [])
    .find(s => s.tree_node_id === stepId);
  const stepIdRef = step?.step_id_ref;

  // Determine checkbox state
  const isCurrent = checkboxState?.currentNodeId === substep.tree_node_id && 
                    checkboxState?.currentNodeType === 'substep';
  const isSourceSubstep = checkboxState?.sourceNodeId === substep.tree_node_id || 
                         checkboxState?.sourceNodeId === substep.substep_id_ref;
  
  // Show checkbox based on move type and source node
  // For source section's/step's substeps, show checkbox but disable it
  const shouldDisable = shouldDisableSubstepCheckbox(substep.tree_node_id, stepId, stepIdRef, sectionId);
  const showCheckbox = shouldShowSubstepCheckbox(substep.tree_node_id, stepId, stepIdRef, sectionId) || shouldDisable;
  const isDisabled = shouldDisable || (moveNodeType === 'substep' && isSourceSubstep) || 
                     (checkboxState?.allChildrenDisabled && !isCurrent) || isSourceSubstep;
  
  // Source node's children should always be checked
  const isChecked = isCurrent || checkboxState?.allChildrenChecked || isSourceSubstep || shouldDisable;

  return (
    <div key={substep.tree_node_id} style={{ marginTop: "20px", clear: "both" }}>
      {/* Always show heading container, conditionally show checkbox */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {showCheckbox && (
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled && !isSourceSubstep}
              onChange={() => {
                if (isSourceSubstep) return;
                if (!isDisabled && customOnNavigate) {
                  customOnNavigate("substep", substep, {
                    substepIndex,
                    stepIndex: substepIndex,
                    sectionIndex,
                    globalStepNumber,
                    substepLetter,
                    sectionHeading: `Section ${sectionIndex + 1}`,
                    stepHeading: `Step ${globalStepNumber}`,
                    substepHeading: globalSubstepLabel,
                    sectionId,
                    stepId,
                    substepId: substep.tree_node_id
                  });
                }
              }}
              style={{ 
                cursor: isDisabled ? "not-allowed" : (isSourceSubstep ? "not-allowed" : "pointer"),
                accentColor: isSourceSubstep ? "black" : undefined,
              }}
            />
          )}
          <h2 
            onClick={() => {
              if (isSourceSubstep) return;
              if (!isDisabled && showCheckbox && customOnNavigate) {
                customOnNavigate("substep", substep, {
                  substepIndex,
                  stepIndex: substepIndex,
                  sectionIndex,
                  globalStepNumber,
                  substepLetter,
                  sectionHeading: `Section ${sectionIndex + 1}`,
                  stepHeading: `Step ${globalStepNumber}`,
                  substepHeading: globalSubstepLabel,
                  sectionId,
                  stepId,
                  substepId: substep.tree_node_id
                });
              }
            }}
            style={{ 
              fontSize: "20px", 
              margin: 0, 
              color: !isDisabled && showCheckbox ? (checkboxLabelColor || undefined) : undefined,
              cursor: isSourceSubstep ? "not-allowed" : ((!isDisabled && showCheckbox) ? "pointer" : "default"),
              userSelect: "none"
            }}
          >{globalSubstepLabel}</h2>
        </div>

        <div
          style={{
            color: "#888",
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          Section {sectionIndex + 1} &gt; Step {globalStepNumber} &gt; Substep{" "}
          {globalStepNumber}{substepLetter}
        </div>
      </div>

      <RenderHtml content={substep.detail?.short_description || ""} />

      {/* Render substep children */}
      {substep.children && substep.children.length > 0 && (
        <>
          {[...substep.children].sort((a, b) => {
            const orderA = parseInt(a.sibling_order || 0, 10);
            const orderB = parseInt(b.sibling_order || 0, 10);
            return orderA - orderB;
          }).map((child, childIndex) => {
            const globalNodeNumber = calculateGlobalNodeNumberByType(child, child.type, preloadedData, compareSiblingOrders);
            return (
              <MoveChildNode
                key={child.tree_node_id}
                child={child}
                parentContext={{
                  sectionIndex,
                  stepIndex: globalStepNumber,
                  substepIndex,
                  substepLetter,
                  sectionId,
                  stepId,
                  substepId: substep.tree_node_id,
                  nodeIndex: childIndex
                }}
                globalNodeNumber={globalNodeNumber || childIndex + 1}
                sectionId={sectionId}
                stepId={stepId}
                substepId={substep.tree_node_id}
                preloadedData={preloadedData}
                checkboxState={checkboxState}
                moveNodeType={moveNodeType}
                checkboxLabelColor={checkboxLabelColor}
                customOnNavigate={customOnNavigate}
                shouldDisableChildCheckbox={shouldDisableChildCheckbox}
                shouldShowChildCheckbox={shouldShowChildCheckbox}
              />
            );
          })}
        </>
      )}
    </div>
  );
};

export default MoveSubstepNode;